import { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Drawer } from 'ypf-pas-general-ui-components';

import { useWorkReportModals } from '../../context/WorkReportModalsContext';
import WorkReportModals from '../WorkReportModals/WorkReportModals';
import { checkRol } from '../../security';
import { inspector } from '../../security/constantsRols';
import NoWorkReports from '../WorkReports/NoWorkReports';

import {
  DayCell,
  Header,
  LocationProvider,
  PositionLine,
  RowDivider,
  TableHeader,
  TableSkeleton
} from './components';
import {
  // isAllChecked,
  isCheckedDailyReport,
  isCheckedGroup,
  isCheckedPosition
} from './utils/isChecked';
import styles from './WeeklyReportTable.module.scss';
import { useFilters, useTable, useSort, useDaysToApproveOrReject, useCalendar } from './hooks';
import WeeklyReportFilters from './components/WeeklyReportFilters/WeeklyReportFilters';
import SubHeader from './components/DayCell/SubHeader/SubHeader';

const renderDayCells = ({
  weeklyReport,
  groupIndex,
  lineIndex,
  secondColumn,
  filters,
  handleDay,
  handleDisableDay,
  isInspector,
  sortType,
  tableData,
  daysIDsToApproveOrReject
}) =>
  weeklyReport.map((dailyReport, dailyReportIndex) => {
    const dayChecked = isCheckedDailyReport({
      groupIndex,
      idParteDiario: dailyReport.idParteDiario,
      lineIndex,
      daysIDsToApproveOrReject
    });

    return (
      <DayCell
        columnNumber={dailyReportIndex + 3}
        dailyReport={dailyReport}
        filters={filters}
        handleDay={handleDay}
        handleDisableDay={handleDisableDay}
        isChecked={dayChecked}
        isInspector={isInspector}
        key={typeof dailyReport === 'string' ? dailyReport : dailyReport.fecha}
        line={`${secondColumn.numeroLinea} - ${secondColumn.lineaSap}`}
        lineID={`${groupIndex}-${lineIndex}-${dailyReportIndex}`}
        locationName={
          sortType === 'location'
            ? tableData[groupIndex].group[0].firstColumn.agrupadoPor
            : tableData[groupIndex].group[lineIndex].secondColumn.agrupadoPor
        }
        measureUnit={secondColumn.unidadMedida}
        position={`${secondColumn.numeroPosicion} - ${secondColumn.posicionSap}`}
      />
    );
  });

const WeeklyReportTable = () => {
  const { showModal, hideModal, props } = useWorkReportModals();

  const isInspector = checkRol(inspector);
  const { changeSortType, sortType } = useSort(isInspector);
  const {
    cleanFilters,
    updateFilters,
    initialFilters,
    hasSessionFilters,
    handleChips,
    handleDeleteChips,
    filters
  } = useFilters(isInspector);

  const { calendar, startDay, updateStartDay } = useCalendar(filters);

  const {
    // fetchAllData,
    handleDisableDay,
    isFetchingNextPage,
    isInitialRender,
    isLoading,
    observerRef,
    reloadTable,
    tableData,
    updateApprovedOrRejectedCells
  } = useTable({ filters, isInspector, sortType, selectedDay: startDay });
  const {
    daysIDsToApproveOrReject,
    getIDsToSend,
    // handleCheckAll,
    handleDay,
    handleGroup,
    handleLine,
    handleResetDaysIDsToApproveOrReject
  } = useDaysToApproveOrReject(tableData, filters);

  const [showFilters, setShowFilters] = useState(!hasSessionFilters);

  const handleUpdateApprovedOrRejectedCells = () => {
    updateApprovedOrRejectedCells(daysIDsToApproveOrReject);
    handleResetDaysIDsToApproveOrReject();
  };

  const handleReloadTable = () => {
    handleResetDaysIDsToApproveOrReject();
    reloadTable();
  };

  const filtersNotApplied = JSON.stringify(initialFilters) === JSON.stringify(filters);

  const getIdsParteDiario = (weeklyReport) =>
    weeklyReport
      .filter((item) => typeof item === 'object' && item != null && 'idParteDiario' in item)
      .map((item) => item.idParteDiario);

  const getTpoUbicacionEnum = (weeklyReport) =>
    weeklyReport
      .filter((item) => typeof item === 'object' && item != null && 'tipoUbicacionEnum' in item)
      .map((item) => item.tipoUbicacionEnum);

  return (
    <>
      <Header
        changeSortType={changeSortType}
        filters={filters}
        handleChips={handleChips}
        handleDeleteChips={handleDeleteChips}
        isInspector={isInspector}
        sortType={sortType}
      />
      {isLoading ? (
        <TableSkeleton initialDay={startDay} isInspector={isInspector} sortType={sortType} />
      ) : (
        <div className={styles.container}>
          {tableData.length > 0 ? (
            <div className={styles['table-container']} id="table-container">
              <table cellPadding={0} className={styles.table}>
                <TableHeader
                  areCheckedDays={!!daysIDsToApproveOrReject.size}
                  calendar={calendar}
                  changeSortType={changeSortType}
                  cleanFilters={cleanFilters}
                  extendedView
                  filledFilter={!filtersNotApplied}
                  filters={filters}
                  getIDsToSend={getIDsToSend}
                  handleReloadTable={handleReloadTable}
                  handleUpdateApprovedOrRejectedCells={handleUpdateApprovedOrRejectedCells}
                  initialDay={startDay}
                  isInspector={isInspector}
                  resetDaysToApproveOrReject={handleResetDaysIDsToApproveOrReject}
                  setShowFilters={setShowFilters}
                  sortType={sortType}
                  updateFilters={updateFilters}
                  updateStartDay={updateStartDay}
                />
                <tbody>
                  {tableData.map(({ key, group }, groupIndex) => (
                    <Fragment key={key}>
                      {group.map(({ firstColumn, secondColumn, weeklyReport }, lineIndex) => {
                        const lineID = `${groupIndex}-${lineIndex}`;

                        const groupChecked = isCheckedGroup({
                          group,
                          daysIDsToApproveOrReject,
                          filters
                        });

                        const positionChecked = isCheckedPosition({
                          daysIDsToApproveOrReject,
                          weeklyReport,
                          filters
                        });

                        const obtainIdsParteDiario = getIdsParteDiario(weeklyReport);
                        const obtainTpoUbicacionEnum = getTpoUbicacionEnum(weeklyReport);

                        return (
                          <Fragment
                            key={`${secondColumn.agrupadoPor}-${secondColumn.posicionSap}-${secondColumn.lineaSap}`}
                          >
                            {firstColumn && (
                              <LocationProvider
                                assignmentType={firstColumn.tipoAsignacion}
                                groupID={groupIndex}
                                handleGroup={handleGroup}
                                handleReloadTable={handleReloadTable}
                                isAvailableToFinish={firstColumn.finaliza}
                                isChecked={groupChecked}
                                isDisabled={firstColumn.isDisabled}
                                isInspector={isInspector}
                                requestData={secondColumn}
                                requestID={firstColumn.idSolicitud}
                                sortType={sortType}
                                title={firstColumn.titulo}
                                titleSortBy={firstColumn.agrupadoPor}
                              />
                            )}
                            <SubHeader
                              dateEnd={filters?.fechaHasta}
                              dateInit={filters?.fechaDesde}
                              handleReloadTable={handleReloadTable}
                              requestData={{
                                ...secondColumn,
                                tipoAsignacion: { id: firstColumn?.tipoAsignacion },
                                tipoUbicacionEnum: obtainTpoUbicacionEnum[0],
                                idParteDiario: obtainIdsParteDiario
                              }}
                              secondColumnData={secondColumn}
                              showHeader={secondColumn.showHeader}
                              sortType={sortType}
                              title={secondColumn.titulo}
                              titleSortBy={secondColumn.agrupadoPor}
                              weeklyReport={group}
                            />

                            <tr
                              className={`${
                                secondColumn.showHeader ? styles['position-container'] : ''
                              } ${group.length - 1 === lineIndex ? styles['bottom-group'] : ''}`}
                            >
                              <PositionLine
                                cambioImputacion={secondColumn.cambioImputacion}
                                date={startDay}
                                editaImputacion={secondColumn.editaImputacion}
                                handleLine={handleLine}
                                isChecked={positionChecked}
                                isDisabled={secondColumn.isDisabled}
                                isInspector={isInspector}
                                line={`${secondColumn.numeroLinea} - ${secondColumn.lineaSap}`}
                                lineID={lineID}
                                measureUnit={secondColumn.unidadMedida}
                                position={`${secondColumn.numeroPosicion} - ${secondColumn.posicionSap}`}
                                requestData={secondColumn}
                                secondColumnData={secondColumn}
                                showHeader={secondColumn.showHeader}
                                sortType={sortType}
                                title={secondColumn.titulo}
                                titleSortBy={secondColumn.agrupadoPor}
                                weeklyReport={weeklyReport}
                              />
                              {renderDayCells({
                                weeklyReport,
                                groupIndex,
                                lineIndex,
                                secondColumn,
                                filters,
                                handleDay,
                                handleDisableDay,
                                isInspector,
                                sortType,
                                tableData,
                                daysIDsToApproveOrReject
                              })}
                            </tr>
                          </Fragment>
                        );
                      })}
                      {groupIndex !== tableData.length - 1 && <RowDivider />}
                    </Fragment>
                  ))}
                </tbody>
              </table>
              <div ref={observerRef} />
              {isFetchingNextPage && <div className={styles.loading}>Cargando...</div>}
              {!isFetchingNextPage && (
                <div className={styles['finished-loading']}>No hay mas datos</div>
              )}
            </div>
          ) : (
            <div className={styles['table-container']} id="table-container">
              <table cellPadding={0} className={styles.table}>
                <TableHeader
                  calendar={calendar}
                  cleanFilters={cleanFilters}
                  extendedView
                  filledFilter={!filtersNotApplied}
                  filters={filters}
                  initialDay={startDay}
                  isInspector={isInspector}
                  setShowFilters={setShowFilters}
                  sortType={sortType}
                  updateFilters={updateFilters}
                  updateStartDay={updateStartDay}
                />
              </table>
              <NoWorkReports
                firstRender={isInitialRender}
                subtitle=" "
                title={
                  isInitialRender
                    ? 'Simplifica tu búsqueda agregando filtros'
                    : 'No encontramos datos'
                }
              />
            </div>
          )}
        </div>
      )}

      <WorkReportModals {...props} onHide={hideModal} showModal={showModal} />
      <Drawer onClose={() => setShowFilters(false)} show={showFilters}>
        <WeeklyReportFilters
          calendar={calendar}
          cleanFilters={cleanFilters}
          filters={filters}
          isInspector={isInspector}
          setShowFilters={setShowFilters}
          updateFilters={updateFilters}
        />
      </Drawer>
    </>
  );
};

export default WeeklyReportTable;

WeeklyReportTable.propTypes = {
  initialDay: PropTypes.string.isRequired
};
